import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, ChevronLeft, Flame, ImageIcon, ListChecks, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { api, DAY_NAMES } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import RestTimer from '@/components/RestTimer';

export default function WorkoutMode() {
    const { dayId } = useParams();
    const navigate = useNavigate();
    const [day, setDay] = useState(null);
    const [routines, setRoutines] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [session, setSession] = useState(null);
    // Map routineId => { done: [bool], reps:[n], time:[n], weight:[n], rpe:[n], notes: '' }
    const [state, setState] = useState({});
    const [showRest, setShowRest] = useState(false);
    const [restSeconds, setRestSeconds] = useState(60);
    const [elapsed, setElapsed] = useState(0);
    const [finished, setFinished] = useState(null);
    const startRef = useRef(Date.now());
    const wakeLockRef = useRef(null);

    // Init: load day, routines, start session, acquire wake lock
    useEffect(() => {
        (async () => {
            try {
                // Get the day info (via days list then filter by template)
                const rs = await api.get(`/days/${dayId}/routines`);
                setRoutines(rs.data);
                // Start session
                const s = await api.post('/workouts/start', { day_id: dayId });
                setSession(s.data);
                // Initialize state
                const st = {};
                rs.data.forEach((r) => {
                    st[r.id] = {
                        done: Array(r.sets).fill(false),
                        reps: Array(r.sets).fill(''),
                        weight: Array(r.sets).fill(''),
                        rpe: Array(r.sets).fill(''),
                        notes: '',
                    };
                });
                setState(st);
                // Try to get day meta (from templates)
                try {
                    const templates = await api.get('/templates');
                    for (const t of templates.data) {
                        const dayList = await api.get(`/templates/${t.id}/days`);
                        const found = dayList.data.find((d) => d.id === dayId);
                        if (found) { setDay(found); break; }
                    }
                } catch (_) {}
                // Wake lock
                if ('wakeLock' in navigator) {
                    try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch (_) {}
                }
            } catch (e) {
                toast.error('No se pudo iniciar el entrenamiento');
                navigate('/');
            }
        })();
        // Timer
        const id = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
        return () => {
            clearInterval(id);
            if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (_) {} }
        };
    }, [dayId, navigate]);

    const current = routines[currentIdx];
    const currentState = current ? state[current.id] : null;
    const totalSets = routines.reduce((acc, r) => acc + r.sets, 0);
    const completedSets = Object.values(state).reduce((acc, s) => acc + s.done.filter(Boolean).length, 0);
    const progressPct = totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

    const logSet = async (r, setIdx, fields) => {
        if (!session) return;
        try {
            await api.post(`/workouts/${session.id}/log-set`, {
                session_id: session.id,
                routine_id: r.id,
                exercise_id: r.exercise_id,
                set_index: setIdx,
                completed: true,
                actual_reps: fields.reps ? parseInt(fields.reps, 10) : null,
                actual_time_sec: fields.time ? parseInt(fields.time, 10) : null,
                weight_kg: fields.weight ? parseFloat(fields.weight) : null,
                rpe: fields.rpe ? parseInt(fields.rpe, 10) : null,
                notes: fields.notes || '',
            });
        } catch (e) {
            toast.error('Error al guardar la serie');
        }
    };

    const toggleSet = async (setIdx) => {
        if (!current) return;
        const s = { ...state };
        const cs = { ...s[current.id] };
        const newDone = !cs.done[setIdx];
        cs.done = [...cs.done];
        cs.done[setIdx] = newDone;
        s[current.id] = cs;
        setState(s);
        if (newDone) {
            const isTime = current.exercise?.type === 'time';
            const timeGuess = isTime ? parseInt(String(current.reps_or_time).replace(/\D/g, ''), 10) || null : null;
            const repsGuess = !isTime ? parseInt(cs.reps[setIdx] || current.reps_or_time, 10) || null : null;
            await logSet(current, setIdx, {
                reps: repsGuess,
                time: timeGuess,
                weight: cs.weight[setIdx],
                rpe: cs.rpe[setIdx],
                notes: cs.notes,
            });
            toast.success('Serie completada');
            // Auto start rest if not the last set
            if (setIdx < current.sets - 1) {
                setRestSeconds(current.rest_seconds || 60);
                setShowRest(true);
            }
        }
    };

    const goNext = () => {
        if (currentIdx < routines.length - 1) {
            setCurrentIdx(currentIdx + 1);
            setShowRest(false);
        }
    };
    const goPrev = () => {
        if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
    };

    const finishWorkout = async () => {
        try {
            const r = await api.post(`/workouts/${session.id}/finish`);
            setFinished(r.data);
            if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (_) {} }
        } catch (e) {
            toast.error('No se pudo finalizar');
        }
    };

    const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
    const ss = String(elapsed % 60).padStart(2, '0');

    if (finished) {
        return <FinishedScreen session={finished} onClose={() => navigate('/')} day={day} />;
    }

    if (!current || !currentState) {
        return (
            <div className="min-h-screen grid place-items-center text-muted-foreground">
                Cargando entrenamiento…
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="workout-active-mode">
            {/* Top strip */}
            <div className="sticky top-0 z-20 bg-[hsl(var(--surface-1))] border-b border-border">
                <div className="px-4 lg:px-8 h-14 flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
                    </Button>
                    <div className="text-sm font-medium">
                        {day ? (day.name || DAY_NAMES[day.day_of_week]) : 'Entrenamiento'}
                        <span className="text-muted-foreground ml-2 tabular-nums">{mm}:{ss}</span>
                    </div>
                    <Button variant="secondary" size="sm" onClick={finishWorkout} data-testid="finish-workout-button">
                        Finalizar
                    </Button>
                </div>
                <Progress value={progressPct} className="h-1 bg-[hsl(var(--surface-2))]" />
            </div>

            {/* Split layout */}
            <div className="lg:grid lg:grid-cols-12 lg:gap-0">
                {/* LEFT: exercise list (only >=lg) */}
                <aside className="hidden lg:flex lg:col-span-4 xl:col-span-3 border-r border-border h-[calc(100vh-3.5rem)] bg-[hsl(var(--surface-1))]">
                    <ScrollArea className="w-full p-4 scroll-soft">
                        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Ejercicios</div>
                        <div className="space-y-2">
                            {routines.map((r, i) => {
                                const doneCount = state[r.id]?.done.filter(Boolean).length || 0;
                                const active = i === currentIdx;
                                return (
                                    <button key={r.id} onClick={() => setCurrentIdx(i)} data-testid={`workout-sidebar-item-${i}`}
                                        className={`w-full text-left p-3 rounded-[12px] border transition-colors flex items-center gap-3 ${
                                            active ? 'bg-[hsl(var(--surface-2))] border-[hsl(var(--primary))]/40' : 'bg-[hsl(var(--surface-2))]/50 border-border hover:bg-[hsl(var(--surface-2))]'
                                        }`}>
                                        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--surface-3))] grid place-items-center text-xs font-medium">{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{r.exercise?.name}</div>
                                            <div className="text-xs text-muted-foreground">{doneCount}/{r.sets} series</div>
                                        </div>
                                        {doneCount === r.sets && <Check className="h-4 w-4 text-[hsl(var(--primary))]" />}
                                    </button>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </aside>

                {/* RIGHT: active exercise */}
                <section className="lg:col-span-8 xl:col-span-9 p-4 lg:p-8 pb-32">
                    <Card className="bg-card border-border p-5 lg:p-6" data-testid="active-exercise-card">
                        <div className="flex items-start justify-between mb-4 gap-4">
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-widest">Ejercicio {currentIdx + 1}/{routines.length}</div>
                                <h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight mt-1" data-testid="active-exercise-name">{current.exercise?.name}</h2>
                                {current.exercise?.muscle_group && <div className="text-xs text-muted-foreground mt-1">{current.exercise.muscle_group}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="secondary" size="sm" onClick={goPrev} disabled={currentIdx === 0} data-testid="previous-exercise-button">
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button size="sm" onClick={goNext} disabled={currentIdx >= routines.length - 1} data-testid="next-exercise-button">
                                    Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>

                        {/* Media */}
                        <div className="rounded-[var(--radius)] overflow-hidden border border-border bg-white mb-5 mx-auto" style={{ maxWidth: '380px', width: '100%' }}>
                            <div className="aspect-square w-full grid place-items-center relative">
                                {current.exercise?.media_url ? (
                                    <>
                                        <img
                                            src={current.exercise.media_url}
                                            alt={current.exercise?.name}
                                            className="w-full h-full object-contain"
                                            data-testid="active-exercise-gif"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const ph = e.currentTarget.nextElementSibling;
                                                if (ph) ph.style.display = 'flex';
                                            }}
                                        />
                                        <div className="absolute inset-0 hidden flex-col items-center justify-center gap-2 text-muted-foreground bg-[hsl(var(--surface-2))]">
                                            <ImageIcon className="h-10 w-10 opacity-40" />
                                            <div className="text-xs">Imagen no disponible</div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-muted-foreground flex flex-col items-center gap-2 bg-[hsl(var(--surface-2))] w-full h-full justify-center">
                                        <ImageIcon className="h-10 w-10 opacity-40" />
                                        <div className="text-xs">Sin imagen (puedes añadir una URL en Ejercicios)</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {current.exercise?.description && (
                            <div className="text-sm text-muted-foreground mb-4">{current.exercise.description}</div>
                        )}

                        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                            <ListChecks className="h-4 w-4" />
                            <span>{current.sets} series · objetivo: <span className="text-foreground font-medium">{current.reps_or_time}{current.exercise?.type === 'time' && !String(current.reps_or_time).endsWith('s') ? 's' : ''}</span> · descanso {current.rest_seconds}s</span>
                        </div>

                        {/* Sets */}
                        <div className="space-y-2 mb-5">
                            {Array.from({ length: current.sets }).map((_, idx) => {
                                const done = currentState.done[idx];
                                const isTime = current.exercise?.type === 'time';
                                return (
                                    <div key={idx} className={`p-3 rounded-[12px] border transition-colors flex items-center gap-2 ${
                                        done ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10' : 'border-border bg-[hsl(var(--surface-2))]'
                                    }`} data-testid={`set-row-${idx}`}>
                                        <div className="w-10 text-center">
                                            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Serie</div>
                                            <div className="font-semibold">{idx + 1}</div>
                                        </div>
                                        {!isTime && (
                                            <div className="w-[84px]">
                                                <Label className="text-[10px] text-muted-foreground">Reps</Label>
                                                <Input inputMode="numeric" placeholder={current.reps_or_time} value={currentState.reps[idx]} onChange={(e) => {
                                                    const s = { ...state };
                                                    s[current.id].reps[idx] = e.target.value;
                                                    setState(s);
                                                }} data-testid={`set-reps-input-${idx}`} className="h-10" />
                                            </div>
                                        )}
                                        <div className="w-[86px]">
                                            <Label className="text-[10px] text-muted-foreground">Peso (kg)</Label>
                                            <Input inputMode="decimal" placeholder="—" value={currentState.weight[idx]} onChange={(e) => {
                                                const s = { ...state };
                                                s[current.id].weight[idx] = e.target.value;
                                                setState(s);
                                            }} data-testid={`set-weight-input-${idx}`} className="h-10" />
                                        </div>
                                        <div className="w-[64px]">
                                            <Label className="text-[10px] text-muted-foreground">RPE</Label>
                                            <Input inputMode="numeric" placeholder="1-10" value={currentState.rpe[idx]} onChange={(e) => {
                                                const s = { ...state };
                                                s[current.id].rpe[idx] = e.target.value;
                                                setState(s);
                                            }} data-testid={`set-rpe-input-${idx}`} className="h-10" />
                                        </div>
                                        <button
                                            onClick={() => toggleSet(idx)}
                                            className={`ml-auto h-12 w-12 rounded-full border-2 grid place-items-center transition-colors flex-shrink-0 ${
                                                done ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))]' : 'bg-[hsl(var(--surface-3))] border-border hover:border-[hsl(var(--primary))]'
                                            }`}
                                            data-testid={`set-checkbox-${idx}`}
                                            aria-label={done ? 'Desmarcar serie' : 'Completar serie'}
                                        >
                                            <Check className={`h-5 w-5 ${done ? 'text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground'}`} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Notes */}
                        <div className="mb-3">
                            <Label className="text-xs text-muted-foreground mb-1 block">Notas del ejercicio</Label>
                            <Textarea placeholder="¿Cómo fue? (opcional)" value={currentState.notes} onChange={(e) => {
                                const s = { ...state };
                                s[current.id].notes = e.target.value;
                                setState(s);
                            }} data-testid="exercise-notes-input" className="min-h-[60px]" />
                        </div>
                    </Card>

                    {/* Rest timer inline */}
                    {showRest && (
                        <div className="mt-5">
                            <RestTimer
                                seconds={restSeconds}
                                onFinish={() => { setShowRest(false); toast.message('Descanso terminado'); }}
                                onSkip={() => setShowRest(false)}
                            />
                        </div>
                    )}
                </section>
            </div>

            {/* Sticky bottom bar mobile */}
            <div className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-[hsl(var(--surface-1))] border-t border-border px-4 py-3 flex items-center gap-2 pb-[calc(env(safe-area-inset-bottom)+12px)]">
                <Button variant="secondary" onClick={goPrev} disabled={currentIdx === 0} className="h-12">
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button onClick={goNext} disabled={currentIdx >= routines.length - 1} className="flex-1 h-12">
                    Siguiente ejercicio <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
            </div>
        </div>
    );
}

function FinishedScreen({ session, day, onClose }) {
    const mm = Math.floor((session.total_time_sec || 0) / 60);
    const ss = (session.total_time_sec || 0) % 60;
    return (
        <div className="min-h-screen grid place-items-center px-4">
            <Card className="bg-card border-border p-8 max-w-md w-full text-center" data-testid="workout-finished-summary">
                <div className="h-16 w-16 rounded-full bg-[hsl(var(--primary))]/15 grid place-items-center mx-auto mb-4">
                    <Trophy className="h-8 w-8 text-[hsl(var(--primary))]" />
                </div>
                <h2 className="text-2xl font-display font-semibold mb-1">¡Entreno completado!</h2>
                <div className="text-sm text-muted-foreground mb-6">{day ? (day.name || DAY_NAMES[day.day_of_week]) : ''}</div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="p-4 rounded-[12px] bg-[hsl(var(--surface-2))] border border-border">
                        <div className="text-xs text-muted-foreground">Tiempo total</div>
                        <div className="text-2xl font-semibold tabular-nums">{mm}:{String(ss).padStart(2, '0')}</div>
                    </div>
                    <div className="p-4 rounded-[12px] bg-[hsl(var(--surface-2))] border border-border">
                        <div className="text-xs text-muted-foreground">Series completadas</div>
                        <div className="text-2xl font-semibold">{session.total_sets_completed}</div>
                    </div>
                </div>
                <Button onClick={onClose} className="w-full h-12" data-testid="workout-finish-close">
                    <Flame className="h-4 w-4 mr-2" /> Volver al inicio
                </Button>
            </Card>
        </div>
    );
}
